import { z } from 'zod';

import { chatInputPartSchema } from '../chat-context.types';

export const sseContract = z.object({
  namespace: z.literal('/api'),
  pathPrefix: z.literal('/:tenantId/sse/'),
  response: z.object({
    content: z.string(),
    id: z.string(),
    timestamp: z.number(),
  }),
  paths: z.object({
    chatInsightsWithCitations: z.object({
      path: z.literal('insights-with-citations'),
      params: z.object({
        tenantId: z.string(),
      }),
      body: z.object({
        message: z
          .object({
            content: z.string().optional(),
            parts: z.array(chatInputPartSchema).optional(),
          })
          .refine(
            (m) =>
              m.content !== undefined ||
              (m.parts !== undefined && m.parts.length > 0),
            {
              message:
                'message must include either `content` or non-empty `parts`',
            },
          ),
        threadId: z.string().optional(),
        patientId: z.string().optional(),
        appointmentId: z.string().uuid().optional(),
        preferenceId: z.string().optional(),
        starterId: z.string().optional(),
      }),
    }),
    planBuilder: z.object({
      path: z.literal('plan-builder'),
      params: z.object({
        tenantId: z.string(),
      }),
      body: z
        .object({
          text: z.string(),
          // Optional attachments for the turn — data-URL file parts. Claude
          // vision's image media types (≤5MB raw per image, ~7MB as base64)
          // plus PDFs (≤10MB raw, ~14MB as base64); ≤5 per message (all
          // mirrored client-side in the composer).
          files: z
            .array(
              z
                .object({
                  mediaType: z.enum([
                    'image/jpeg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                    'application/pdf',
                  ]),
                  filename: z.string().optional(),
                  url: z
                    .string()
                    .refine(
                      (u) =>
                        u.startsWith('data:image/') ||
                        u.startsWith('data:application/pdf'),
                      { message: 'url must be an image or PDF data URL' },
                    ),
                })
                // Size ceiling is per media type so an oversized image can't
                // ride the larger PDF limit: PDFs ≤10MB raw (~14MB base64),
                // images ≤5MB raw for Claude vision (~7MB base64).
                .refine(
                  (f) =>
                    f.url.length <=
                    (f.mediaType === 'application/pdf'
                      ? 14_500_000
                      : 7_000_000),
                  { message: 'Attachment exceeds the size limit for its type' },
                ),
            )
            .max(5)
            .optional(),
          // Stable per-conversation id (the Mastra memory thread key). The
          // draft is conversation state, so `?thread=` deep links restore it.
          threadId: z.string().min(1),
        })
        // A turn needs something to say: text, a file, or both.
        .refine((v) => v.text.length > 0 || (v.files?.length ?? 0) > 0, {
          message: 'Message must include text or a file',
        })
        // Combined attachment payload stays under the 25mb API body limit
        // (data-URL chars ≈ bytes); mirrors the composer's aggregate cap so a
        // client-valid batch never trips the Express json parser. 22 * 1024^2.
        .refine(
          (v) =>
            (v.files ?? []).reduce((sum, f) => sum + f.url.length, 0) <=
            23_068_672,
          { message: 'Attachments exceed the combined size limit' },
        ),
    }),
    // The Care Plan Tailor chat (ENG-4699): tailors the appointment's draft
    // CarePlan via conversation. Scoped to one appointment; the draft is
    // conversation state restored via `?thread=`.
    carePlanTailor: z.object({
      path: z.literal('care-plan-tailor'),
      params: z.object({
        tenantId: z.string(),
      }),
      body: z.object({
        text: z.string().min(1),
        // The appointment whose draft CarePlan is being tailored.
        appointmentId: z.string().uuid(),
        // Stable per-conversation id (the Mastra memory thread key).
        threadId: z.string().min(1),
      }),
    }),
  }),
});

type SseContract = z.infer<typeof sseContract>;
export type SseKeys = keyof SseContract['paths'];

export const getSseFullPath = (route: SseKeys) =>
  `${sseContract.shape.pathPrefix.value}${sseContract.shape.paths.shape[route].shape.path.value}`;

export const getEndpointUrl = <T extends SseKeys>(
  baseUrl: string,
  route: T,
  args?: { params: SseContract['paths'][T]['params'] },
): string => {
  const params = args?.params;
  const schema = sseContract.shape.paths.shape[route];
  let path = `${sseContract.shape.namespace.value}${sseContract.shape.pathPrefix.value}${schema.shape.path.value}`;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`:${key}`, String(value));
    }
  }
  return `${baseUrl}${path}`;
};
